import { useEffect, useState } from "react";
import {
  CarFront,
  TrendingUp,
  KeyRound,
  AlertTriangle,
  Clock3,
  Receipt,
  Wallet,
  BadgeCheck,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { api, apiErrorMessage } from "../lib/api";
import { currencyTRY, numberTR } from "../lib/format";
import { toast } from "sonner";
import { Skeleton } from "../components/ui/skeleton";
import Reminders from "../components/Reminders";
import SubscriptionBanner from "../components/SubscriptionBanner";

function KpiCard({ label, value, sub, icon: Icon, tone = "neutral", testid }) {
  const toneMap = {
    neutral: "bg-white border-stone-200",
    danger: "bg-rose-50/60 border-rose-200",
    warn: "bg-amber-50/60 border-amber-200",
    good: "bg-emerald-50/60 border-emerald-200",
  };
  const iconTone = {
    neutral: "bg-stone-100 text-slate-700",
    danger: "bg-rose-100 text-rose-700",
    warn: "bg-amber-100 text-amber-800",
    good: "bg-emerald-100 text-emerald-700",
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border p-5 transition-transform hover:-translate-y-0.5 ${toneMap[tone]}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-stone-500">
            {label}
          </div>
          <div className="mt-2 font-display font-black text-2xl md:text-3xl text-slate-900 num-tabular">
            {value}
          </div>
          {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
        </div>
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconTone[tone]}`}
        >
          <Icon className="w-5 h-5" strokeWidth={1.8} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/analytics/dashboard");
        setData(res.data);
      } catch (err) {
        toast.error(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }

  if (!data) return null;

  const pnlMax = Math.max(
    1,
    ...data.monthly_pnl.map((m) => Math.abs(m.net) || 0),
  );

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <SubscriptionBanner />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-stone-500">
            Özet
          </div>
          <h1 className="mt-1 font-display font-black text-3xl md:text-5xl text-slate-900">
            Galerinin bugünü,
            <br className="hidden md:block" />
            <span className="text-orange-700"> tek bakışta.</span>
          </h1>
        </div>
        <div className="text-sm text-stone-500 num-tabular">
          {new Intl.DateTimeFormat("tr-TR", {
            weekday: "long",
            day: "2-digit",
            month: "long",
            year: "numeric",
          }).format(new Date())}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-stok"
          label="Stoktaki Araç"
          value={numberTR(data.stok_adet)}
          sub={`${currencyTRY(data.stok_deger)} toplam değer`}
          icon={CarFront}
        />
        <KpiCard
          testid="kpi-ay-satis"
          label="Bu Ay Satış"
          value={numberTR(data.ay_satis_adet)}
          sub={`${currencyTRY(data.ay_kar)} net kâr`}
          icon={TrendingUp}
          tone={data.ay_kar >= 0 ? "good" : "danger"}
        />
        <KpiCard
          testid="kpi-aktif-kir"
          label="Aktif Kiralama"
          value={numberTR(data.aktif_kiralama)}
          sub={data.rezerve_adet ? `${data.rezerve_adet} rezerve araç` : "Filoda"}
          icon={KeyRound}
        />
        <KpiCard
          testid="kpi-gecikmis"
          label="Gecikmiş İade"
          value={numberTR(data.gecikmis_iade)}
          sub={
            data.gecikmis_iade > 0
              ? "Acil takip gerekli"
              : "Gecikmiş kiralama yok"
          }
          icon={AlertTriangle}
          tone={data.gecikmis_iade > 0 ? "danger" : "neutral"}
        />
        <KpiCard
          testid="kpi-bugun"
          label="Bugün İade"
          value={numberTR(data.bugun_iade)}
          sub="Bugün teslim alınacak"
          icon={Clock3}
          tone={data.bugun_iade > 0 ? "warn" : "neutral"}
        />
        <KpiCard
          testid="kpi-ay-gider"
          label="Bu Ay Gider"
          value={currencyTRY(data.ay_gider)}
          sub="İşletme giderleri"
          icon={Receipt}
        />
        <KpiCard
          testid="kpi-ciro"
          label="Toplam Ciro"
          value={currencyTRY(data.toplam_ciro)}
          sub={`Ort. stok günü: ${numberTR(data.ortalama_stok_gun)}`}
          icon={Wallet}
        />
        <KpiCard
          testid="kpi-kar"
          label="Toplam Net Kâr"
          value={currencyTRY(data.toplam_kar)}
          sub={data.toplam_kar >= 0 ? "Kârdasın" : "Zararda"}
          icon={BadgeCheck}
          tone={data.toplam_kar >= 0 ? "good" : "danger"}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-stone-200 bg-white p-5 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-widest text-stone-500">
                Son 6 Ay
              </div>
              <h3 className="font-display font-black text-xl text-slate-900 mt-1">
                Aylık Net Kâr / Zarar
              </h3>
            </div>
            <div className="flex items-center gap-3 text-xs text-stone-500">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />
                Pozitif
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-rose-500" />
                Negatif
              </span>
            </div>
          </div>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={data.monthly_pnl} barCategoryGap={16}>
                <CartesianGrid stroke="#f5f5f4" vertical={false} />
                <XAxis dataKey="ay" axisLine={false} tickLine={false} />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) =>
                    Math.abs(v) >= 1000
                      ? `${(v / 1000).toFixed(0)}b`
                      : v.toString()
                  }
                  width={50}
                />
                <Tooltip
                  cursor={{ fill: "#fafaf9" }}
                  contentStyle={{
                    borderRadius: 12,
                    border: "1px solid #e7e5e4",
                    fontFamily: "Karla",
                  }}
                  formatter={(v) => [currencyTRY(v), "Net"]}
                />
                <Bar dataKey="net" radius={[8, 8, 8, 8]} maxBarSize={44}>
                  {data.monthly_pnl.map((m, i) => (
                    <Cell
                      key={i}
                      fill={m.net >= 0 ? "#059669" : "#e11d48"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-white p-5 md:p-6">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-stone-500">
            Marka Dağılımı
          </div>
          <h3 className="font-display font-black text-xl text-slate-900 mt-1 mb-4">
            Satılan Araçlar
          </h3>
          {data.marka_dagilim.length === 0 ? (
            <div className="text-sm text-stone-500 py-10 text-center">
              Henüz satış yok.
            </div>
          ) : (
            <div className="space-y-3">
              {data.marka_dagilim.map((m) => {
                const max = Math.max(
                  ...data.marka_dagilim.map((x) => x.adet),
                );
                const pct = (m.adet / max) * 100;
                return (
                  <div key={m.marka}>
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-slate-800">
                        {m.marka}
                      </span>
                      <span className="text-stone-500 num-tabular">
                        {m.adet} adet
                      </span>
                    </div>
                    <div className="mt-1.5 h-2 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-slate-900 rounded-full"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Reminders />
    </div>
  );
}
